import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { arrayMove } from '@dnd-kit/sortable';
import { WorkType } from 'types/project/projectDefaults';
import { ProjectUser } from 'types/project';
import { Task } from 'types/project/tasks';
import { User } from 'types/task';
import { useDebounce } from 'hooks/useDebounce';
import { useGetAllTasksQuery } from 'service/project/hooks/useGetAllTasksQuery';
import { useReorderWorkItemMutation } from 'service/project/hooks/useReorderWorkItem';
import { useQueryClient } from '@tanstack/react-query';
import TaskFilterDropdown from './ProjectDetails/TaskFilterDropdown';
import EnhancedDeleteModal from '../../Modals/EnhancedDeleteModal';
import ChildItemsHeader from './ChildItems/ChildItemsHeader';
import ChildItemsCreateForm from './ChildItems/ChildItemsCreateForm';
import ChildItemsLinkCombobox from './ChildItems/ChildItemsLinkCombobox';
import ChildItemsTable from './ChildItems/ChildItemsTable';

// Using a partial representation of the DTO required by the API
export interface ChildItemDraft {
  id: string; // temp id for UI
  workItemUuid?: string;
  title: string;
  workItemKey?: string;
  workStatusUuid?: string;
  workStatusName?: string;
  workStatusCategory?: string;
  workTypeUuid: string | null;
  workTypeCategory: string | null;
  assigneeUuid: string | null;
  reporterUuid?: string | null;
  priority: 'low' | 'medium' | 'high' | string;
  totalSubTasksCount?: number;
  totalChildWorkItemsCount?: number;
  totalCommentsCount?: number;
  totalMediaCount?: number;
  childTotalElements?: number;
  childWorkItems?: any[];
  // This item's own listVersion, for its direct children — required as
  // expectedListVersion when reordering one of them.
  listVersion?: number;
}

interface ChildItemsListProps {
  parentTaskTypeCategory: 'EPIC' | 'STORY' | 'TASK' | 'BUG' | 'SUB_TASK';
  childItems: ChildItemDraft[];
  isLoading?: boolean;
  onChildrenChange: (items: ChildItemDraft[]) => void;
  availableWorkTypes: WorkType[];
  availableWorkStatuses?: any[];
  availableUsers: ProjectUser[];
  disabled?: boolean;
  /** Lists the children only: no adding, editing, reordering or removing. */
  readOnly?: boolean;
  onAddChild?: (title: string, workTypeUuid: string, assigneeUuid: string | null, priority: string, existingTaskUuid?: string, existingTaskData?: { workStatusUuid?: string; workPriority?: string; workItemAssigneeAppUserUuid?: string | null; workItemReporterAppUserUuid?: string | null; workItemDescription?: string; projectLabelUuidList?: any[] }) => Promise<boolean>;
  onUpdateChildItem?: (childTaskId: string, updates: Partial<any>) => void;
  onDeleteChild?: (childTaskId: string) => Promise<boolean>;
  isRemoving?: boolean;
  projectKey?: string;
  projectId?: string;
  currentTaskUuid?: string;
  childTotalElements?: number;
  childCurrentPage?: number;
  childTotalPages?: number;
  // currentTaskUuid's own listVersion, for the root-level childItems list —
  // required as expectedListVersion when reordering a root-level child.
  childListVersion?: number;
  onLoadMoreChildren?: () => void;
  isLoadingMore?: boolean;
  onExpandChild?: (parentUuid: string) => void;
  loadingParents?: Set<string>;
  totalOpenWorkItemCount?: number;
  totalInProgressWorkItemCount?: number;
  totalDoneWorkItemCount?: number;
  isCompact?: boolean;
  activeFilters?: any;
  onApplyFilters?: (filters: any) => void;
  totalChildWorkItemsCount?: number;
}

// Finds the sibling array containing targetId, plus the parent item that
// array belongs to (null at the root). Shared by every reorder handler,
// since all three need the parent to know which listVersion to send as
// expectedListVersion — the root list's version is a prop (the current
// task's own listVersion), a nested list's version lives on its parent item.
const findSiblingsWithParent = (
  items: any[],
  targetId: string,
  parent: any | null = null,
): { siblings: any[]; parent: any | null } | null => {
  if (items.some((i) => (i.workItemUuid || i.id) === targetId)) return { siblings: items, parent };
  for (const item of items) {
    if (item.childWorkItems?.length) {
      const result = findSiblingsWithParent(item.childWorkItems, targetId, item);
      if (result) return result;
    }
  }
  return null;
};

const countAllNestedChildren = (task: any): number => {
  if (!task) return 0;
  const directChildren = task.childWorkItems || [];
  let count = directChildren.length;
  if (task.childTotalElements && task.childTotalElements > count) {
    count = task.childTotalElements;
  }
  const backendCount = task.totalChildWorkItemsCount || task.totalSubTasksCount || 0;
  if (backendCount > count) {
    count = backendCount;
  }
  directChildren.forEach((child: any) => {
    count += countAllNestedChildren(child);
  });
  return count;
};

const ChildItemsList: React.FC<ChildItemsListProps> = React.memo(({
  parentTaskTypeCategory,
  childItems,
  onChildrenChange,
  availableWorkTypes,
  availableUsers,
  disabled = false,
  readOnly = false,
  onAddChild,
  onUpdateChildItem,
  onDeleteChild,
  isRemoving = false,
  projectKey,
  projectId,
  currentTaskUuid,
  availableWorkStatuses = [],
  childTotalElements = 0,
  childCurrentPage,
  childTotalPages,
  childListVersion,
  onLoadMoreChildren,
  isLoadingMore = false,
  onExpandChild,
  loadingParents,
  totalOpenWorkItemCount = 0,
  totalInProgressWorkItemCount = 0,
  totalDoneWorkItemCount = 0,
  isCompact = false,
  activeFilters: externalActiveFilters,
  onApplyFilters,
  isLoading = false,
  totalChildWorkItemsCount: externalTotalCount,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const debouncedSearch = useDebounce(inputValue, 500);
  const [expandedMode, setExpandedMode] = useState<'create' | 'link' | null>(null);

  const allowedChildCategories: string[] = useMemo(() => {
    switch (parentTaskTypeCategory) {
      case 'EPIC':  return ['STORY', 'TASK', 'BUG'];
      case 'STORY': return ['TASK', 'BUG'];
      case 'TASK':
      case 'BUG':   return ['SUB_TASK'];
      default:      return ['STORY', 'TASK', 'BUG', 'SUB_TASK'];
    }
  }, [parentTaskTypeCategory]);

  const isSubtaskMode = parentTaskTypeCategory === 'TASK' || parentTaskTypeCategory === 'BUG';

  // Resolve which UUIDs are valid for the current hierarchy and linking rules
  const allowedLinkableTypeUuids = useMemo(() => {
    return availableWorkTypes
      .filter(wt =>
        allowedChildCategories.includes(wt.workTypeCategory) &&
        ['STORY', 'TASK', 'BUG'].includes(wt.workTypeCategory)
      )
      .map(wt => wt.workTypeUuid);
  }, [availableWorkTypes, allowedChildCategories]);

  // Fetch existing tasks with backend filtering
  const { data: taskResponse } = useGetAllTasksQuery({
    projectUuid: projectId || '',
    search: debouncedSearch || undefined,
    workTypeUuidList: allowedLinkableTypeUuids.length > 0 ? allowedLinkableTypeUuids : ['NONE'],
    cursor: null,
    size: 50,
    viewType: 'LIST',
  });
  const tasksList = taskResponse?.response?.[0]?.data || [];

  const [isAdding, setIsAdding] = useState(false);
  const [isMovingToBottom, setIsMovingToBottom] = useState(false);
  const { mutate: reorderTask, isPending: isReorderPending } = useReorderWorkItemMutation();
  const queryClient = useQueryClient();

  // Shared by every optimistic reorder (Move to Top/Bottom and drag-and-drop) —
  // flattens the paginated cache into one list, lets the caller reorder it
  // however it needs to, then re-splits it back across the same page
  // boundaries so "Show More" pagination keeps working untouched.
  //
  // Matches the query key by prefix only (projectId + task), not the full key
  // useTaskDetailManagement actually uses — its page size starts at 20 but
  // gets bumped to match childItems.length once more than 20 items are
  // loaded (see the effect there keyed on childItems.length), so hardcoding
  // 20 here would silently miss the real, active query once "Show More" has
  // been used and write into an orphaned cache entry nothing reads.
  const applyOptimisticChildOrder = (reorderChildren: (allChildren: any[]) => any[]) => {
    queryClient.setQueriesData({ queryKey: ['task-children-infinite', projectId, currentTaskUuid], exact: false }, (old: any) => {
      if (!old || !old.pages || old.pages.length === 0) return old;

      let allChildren: any[] = [];
      const pageChildrenCounts: number[] = [];

      old.pages.forEach((page: any) => {
        const pObj = Array.isArray(page.response) ? page.response[0] : page.response;
        const items = pObj?.childWorkItems || pObj?.data || [];
        allChildren = [...allChildren, ...items];
        pageChildrenCounts.push(items.length);
      });

      const reorderedChildren = reorderChildren(allChildren);

      let currentIndex = 0;
      const newPages = old.pages.map((page: any, idx: number) => {
        const count = pageChildrenCounts[idx];
        const pageItems = reorderedChildren.slice(currentIndex, currentIndex + count);
        currentIndex += count;

        const isArrayResponse = Array.isArray(page.response);
        const pObj = isArrayResponse ? page.response[0] : page.response;
        const updatedPObj = {
          ...pObj,
          ...(pObj?.childWorkItems !== undefined && { childWorkItems: pageItems }),
          ...(pObj?.data !== undefined && { data: pageItems }),
        };

        return {
          ...page,
          response: isArrayResponse ? [updatedPObj] : updatedPObj,
        };
      });

      return { ...old, pages: newPages };
    });
  };

  // A successful reorder's response carries the parent's new listVersion —
  // applied to the cache immediately so the very next reorder in the same
  // list sends the right expectedListVersion. Waiting for the mutation's own
  // background refetch (on a ~1s delay) would leave a window where
  // isReorderPending has already cleared but the cache hasn't caught up,
  // so a fast second reorder would send a now-stale version and get an
  // unnecessary 409 even though nothing else actually changed the list.
  const applyListVersionBump = (parentId: string | null, newVersion: number) => {
    queryClient.setQueriesData(
      { queryKey: ['task-children-infinite', projectId, currentTaskUuid], exact: false },
      (old: any) => {
        if (!old || !old.pages || old.pages.length === 0) return old;

        const bumpInTree = (items: any[]): any[] =>
          items.map((item) => {
            const updated =
              (item.workItemUuid || item.id) === parentId ? { ...item, listVersion: newVersion } : item;
            if (updated.childWorkItems?.length) {
              return { ...updated, childWorkItems: bumpInTree(updated.childWorkItems) };
            }
            return updated;
          });

        const newPages = old.pages.map((page: any) => {
          const isArrayResponse = Array.isArray(page.response);
          const pObj = isArrayResponse ? page.response[0] : page.response;
          if (!pObj) return page;

          // Root-level: the response's own top-level listVersion describes
          // this list (currentTaskUuid's direct children).
          if (parentId === null) {
            const updatedPObj = { ...pObj, listVersion: newVersion };
            return { ...page, response: isArrayResponse ? [updatedPObj] : updatedPObj };
          }

          const items = pObj.childWorkItems || pObj.data;
          if (!items) return page;
          const updatedItems = bumpInTree(items);
          const updatedPObj = {
            ...pObj,
            ...(pObj.childWorkItems !== undefined && { childWorkItems: updatedItems }),
            ...(pObj.data !== undefined && { data: updatedItems }),
          };
          return { ...page, response: isArrayResponse ? [updatedPObj] : updatedPObj };
        });

        return { ...old, pages: newPages };
      },
    );
  };

  const optimisticallyReorderChildInCache = (targetTaskId: string, direction: 'TOP' | 'BOTTOM') => {
    applyOptimisticChildOrder((allChildren) => {
      const targetItem = allChildren.find(c => (c.workItemUuid || c.id) === targetTaskId);
      if (!targetItem) return allChildren;
      const remainingChildren = allChildren.filter(c => (c.workItemUuid || c.id) !== targetTaskId);
      return direction === 'TOP'
        ? [targetItem, ...remainingChildren]
        : [...remainingChildren, targetItem];
    });
  };

  // Drag-and-drop only ever reorders within whatever's currently loaded (the
  // cache's own flattened list, same scope dnd-kit renders) — it never needs
  // to know about pages that haven't loaded yet, because you can't drag onto
  // a row that isn't rendered.
  const optimisticallyMoveChildToPosition = (activeId: string, overId: string) => {
    applyOptimisticChildOrder((allChildren) => {
      const oldIndex = allChildren.findIndex(c => (c.workItemUuid || c.id) === activeId);
      const newIndex = allChildren.findIndex(c => (c.workItemUuid || c.id) === overId);
      if (oldIndex === -1 || newIndex === -1) return allChildren;
      return arrayMove(allChildren, oldIndex, newIndex);
    });
  };

  // Every reorder (Move to Top/Bottom and drag) applies its new order
  // optimistically before the request resolves — if it then fails, that
  // optimistic state would otherwise stick around indefinitely, silently
  // showing an order that was never actually saved. Falling back to a
  // refetch of the real server order (plus a toast) instead of trying to
  // hand-reconstruct the pre-reorder state, since that reconstruction is
  // itself a second thing that could get out of sync.
  const handleReorderError = (error: any) => {
    // REORDER_CONFLICT means someone else changed this list between when we
    // read its listVersion and when this request reached the server —
    // nothing was saved there either way, but it's a different situation
    // from an outright failure, so it gets its own message.
    const isConflict = error?.errorCode === 'REORDER_CONFLICT';
    toast.error(
      isConflict
        ? 'This list was changed by someone else. Showing the latest order.'
        : 'Failed to save the new order. Reloading the list.',
    );
    queryClient.invalidateQueries({ queryKey: ['task-children-infinite', projectId, currentTaskUuid], exact: false });
  };

  const handleMoveToTop = (taskId: string) => {
    // Block a second reorder from starting while one's still in flight — two
    // overlapping reorders would each compute their new neighbors from the
    // same pre-reorder snapshot, so the second one's positioning could be
    // stale by the time it's sent.
    if (!projectId || !childItems.length || isReorderPending) return;

    const found = findSiblingsWithParent(childItems, taskId);
    if (!found || found.siblings.length <= 1) return;
    const { siblings, parent } = found;

    const targetIndex = siblings.findIndex(s => (s.workItemUuid || s.id) === taskId);
    if (targetIndex <= 0) return;

    const targetItem = siblings[targetIndex];
    const statusUuid = targetItem.workStatusUuid || '';

    // Apply instant optimistic update!
    optimisticallyReorderChildInCache(taskId, 'TOP');

    reorderTask({
      projectUuid: projectId,
      workItemUuid: taskId,
      prevWorkItemUuid: null,
      nextWorkItemUuid: null,
      viewType: 'LIST',
      moveToTop: true,
      newStatusUuid: statusUuid,
      previousStatusUuid: statusUuid,
      expectedListVersion: parent ? parent.listVersion : childListVersion,
    }, {
      onError: handleReorderError,
      onSuccess: (data) => {
        if (data.listVersion !== undefined) {
          applyListVersionBump(parent ? (parent.workItemUuid || parent.id) : null, data.listVersion);
        }
      },
    });
  };

  const handleMoveToBottom = (taskId: string) => {
    if (!projectId || !childItems.length || isReorderPending) return;

    const found = findSiblingsWithParent(childItems, taskId);
    if (!found || found.siblings.length <= 1) return;
    const { siblings, parent } = found;

    const targetIndex = siblings.findIndex(s => (s.workItemUuid || s.id) === taskId);
    if (targetIndex === -1 || targetIndex === siblings.length - 1) return;

    const targetItem = siblings[targetIndex];
    const statusUuid = targetItem.workStatusUuid || '';

    // Disable optimistic update for Move to Bottom.
    // Set loading state to show table skeleton instead.
    setIsMovingToBottom(true);

    reorderTask({
      projectUuid: projectId,
      workItemUuid: taskId,
      prevWorkItemUuid: null,
      nextWorkItemUuid: null,
      viewType: 'LIST',
      moveToBottom: true,
      newStatusUuid: statusUuid,
      previousStatusUuid: statusUuid,
      expectedListVersion: parent ? parent.listVersion : childListVersion,
    }, {
      onError: handleReorderError,
      onSuccess: (data) => {
        if (data.listVersion !== undefined) {
          applyListVersionBump(parent ? (parent.workItemUuid || parent.id) : null, data.listVersion);
        }
      },
      onSettled: () => {
        setIsMovingToBottom(false);
      },
    });
  };

  // Drag-and-drop was previously disabled outright because the list is
  // paginated — but whatever's currently loaded (via "Show More") is still
  // one contiguous, in-order block, so reordering within it is well-defined:
  // the backend positions a work item by its new immediate neighbors
  // (prevWorkItemUuid/nextWorkItemUuid), the same mechanism Move to Top/
  // Bottom already use. The one case that's genuinely ambiguous is dropping
  // an item as the new last row while more pages are still unloaded — we
  // don't know what the real next item is yet, so that drop is rejected
  // with an explanatory toast instead of guessing (sending nextWorkItemUuid:
  // null there would mean "true bottom", jumping past unseen items).
  const handleReorderChild = (activeId: string, overId: string) => {
    if (!projectId || activeId === overId || isReorderPending) return;

    const found = findSiblingsWithParent(childItems, activeId);
    if (!found) return;
    const { siblings, parent } = found;
    if (siblings.length <= 1) return;

    const oldIndex = siblings.findIndex((s) => (s.workItemUuid || s.id) === activeId);
    const overIndex = siblings.findIndex((s) => (s.workItemUuid || s.id) === overId);
    if (oldIndex === -1 || overIndex === -1) return;

    const activeItem = siblings[oldIndex];
    const reordered = arrayMove(siblings, oldIndex, overIndex);
    const newIndex = reordered.findIndex((s) => (s.workItemUuid || s.id) === activeId);
    const prevItem = newIndex > 0 ? reordered[newIndex - 1] : null;
    const nextItem = newIndex < reordered.length - 1 ? reordered[newIndex + 1] : null;

    // At the root, childCurrentPage/childTotalPages come straight from the
    // infinite query's own hasNextPage — more reliable than a count field,
    // which can drift (e.g. a stale totalChildWorkItemsCount after items are
    // added/removed elsewhere). Nested groups don't paginate the same way,
    // so they fall back to comparing the parent's own count against what's
    // actually been loaded into it.
    const hasMoreUnloaded = parent
      ? siblings.length < (parent.childTotalElements || parent.totalChildWorkItemsCount || parent.totalSubTasksCount || siblings.length)
      : childCurrentPage !== undefined && childTotalPages !== undefined
        ? childCurrentPage + 1 < childTotalPages
        : siblings.length < (childTotalElements || siblings.length);

    if (!nextItem && hasMoreUnloaded) {
      toast.error('Load the rest of the list before moving an item to the very end.');
      return;
    }

    const statusUuid = activeItem.workStatusUuid || '';

    optimisticallyMoveChildToPosition(activeId, overId);

    reorderTask({
      projectUuid: projectId,
      workItemUuid: activeId,
      prevWorkItemUuid: (prevItem?.workItemUuid || prevItem?.id) ?? null,
      nextWorkItemUuid: (nextItem?.workItemUuid || nextItem?.id) ?? null,
      viewType: 'LIST',
      newStatusUuid: statusUuid,
      previousStatusUuid: statusUuid,
      expectedListVersion: parent ? parent.listVersion : childListVersion,
    }, {
      onError: handleReorderError,
      onSuccess: (data) => {
        if (data.listVersion !== undefined) {
          applyListVersionBump(parent ? (parent.workItemUuid || parent.id) : null, data.listVersion);
        }
      },
    });
  };

  // Temporary state for the dropdowns before 'Add' is clicked
  const [draftTypeUuid, setDraftTypeUuid] = useState<string | null>(null);
  const [draftAssigneeUuid, setDraftAssigneeUuid] = useState<string | null>(null);
  const [draftPriority] = useState<'low' | 'medium' | 'high' | string>('medium');

  // Filter state
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const CHILD_FILTER_STORAGE_KEY = (key: string) => `pmo_child_filters_${key}`;

  const loadChildFiltersFromStorage = (key: string) => {
    try {
      const raw = sessionStorage.getItem(CHILD_FILTER_STORAGE_KEY(key));
      if (raw) {
        const parsed = JSON.parse(raw);
        parsed.hideCompleted = localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true';
        return parsed;
      }
    } catch (_) {}
    return {
      statuses: [],
      assignees: null,
      priorities: [],
      reporters: null,
      taskTypes: [],
      dueDateRange: { from: null, to: null },
      hideCompleted: key ? localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true' : false,
      // Drag-and-drop reordering is disabled now that this list is paginated
      // (see onReorderWorkItem={undefined} in ChildItemsTable) — defaulting
      // to Newest First instead means a newly added child always lands at
      // the top, without needing manual reordering across pages.
      sortBy: 'createdDate',
      sortDirection: 'DESC',
    };
  };

  const saveChildFiltersToStorage = (key: string, filters: any) => {
    try {
      sessionStorage.setItem(CHILD_FILTER_STORAGE_KEY(key), JSON.stringify(filters));
    } catch (_) {}
  };

  const [localActiveFilters, setLocalActiveFilters] = useState<any>(() => {
    const key = currentTaskUuid || projectKey;
    const defaults = {
      statuses: [],
      assignees: null,
      priorities: [],
      reporters: null,
      taskTypes: [],
      dueDateRange: { from: null, to: null },
      hideCompleted: false,
      sortBy: 'createdDate',
      sortDirection: 'DESC',
    };
    if (key) {
      const restored = loadChildFiltersFromStorage(key);
      return {
        ...restored,
        hideCompleted: localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true',
        _key: key,
      };
    }
    return {
      ...defaults,
      _key: null,
    };
  });

  // Reset/Load task-specific settings
  useEffect(() => {
    const key = currentTaskUuid || projectKey;
    if (key && localActiveFilters._key !== key) {
      setLocalActiveFilters(() => {
        const restored = loadChildFiltersFromStorage(key);
        return {
          ...restored,
          hideCompleted: localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true',
          _key: key,
        };
      });
    }
  }, [currentTaskUuid, projectKey, localActiveFilters._key]);

  // Persist local hide completed preference per task/project
  useEffect(() => {
    const key = currentTaskUuid || projectKey;
    if (key && localActiveFilters._key === key) {
      const { _key, ...filtersToSave } = localActiveFilters;
      saveChildFiltersToStorage(key, filtersToSave);
      localStorage.setItem(`pmo_hide_completed_children_${key}`, String(localActiveFilters.hideCompleted));
    }
  }, [localActiveFilters, currentTaskUuid, projectKey]);

  const activeFilters = externalActiveFilters || localActiveFilters;
  const setActiveFilters = onApplyFilters || setLocalActiveFilters;

  // Deletion Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  const handleApplyFilters = (filters: any) => setActiveFilters((prev: any) => ({ ...prev, ...filters }));
  const handleClearFilters = () => {
    const key = currentTaskUuid || projectKey;
    if (key) {
      try {
        sessionStorage.removeItem(CHILD_FILTER_STORAGE_KEY(key));
      } catch (_) {}
    }
    setActiveFilters((prev: any) => ({
      ...prev,
      statuses: [],
      assignees: null,
      priorities: [],
      reporters: null,
      taskTypes: [],
      createdDateRange: { from: null, to: null },
      dueDateRange: { from: null, to: null },
    }));
  };

  const hasActiveFilters =
    activeFilters.statuses.length > 0 ||
    activeFilters.assignees !== null ||
    activeFilters.priorities.length > 0 ||
    activeFilters.taskTypes.length > 0 ||
    activeFilters.reporters !== null ||
    activeFilters.createdDateRange?.from ||
    activeFilters.createdDateRange?.to ||
    activeFilters.dueDateRange?.from ||
    activeFilters.dueDateRange?.to;

  const getActiveFilterCount = () => {
    let count =
      activeFilters.statuses.length +
      (activeFilters.assignees ? 1 : 0) +
      activeFilters.priorities.length +
      activeFilters.taskTypes.length +
      (activeFilters.reporters ? 1 : 0);
    if (activeFilters.createdDateRange?.from || activeFilters.createdDateRange?.to) count++;
    if (activeFilters.dueDateRange?.from || activeFilters.dueDateRange?.to) count++;
    return count;
  };

  const allowedChildTypes = availableWorkTypes.filter(wt =>
    allowedChildCategories.includes(wt.workTypeCategory)
  );

  const handleAddChild = async () => {
    if (!inputValue.trim() || isAdding) return;

    const selectedType = (selectedTask ? selectedTask.workTypeUuid : (draftTypeUuid || allowedChildTypes[0]?.workTypeUuid)) || '';
    const selectedAssignee = (selectedTask ? selectedTask.workItemAssigneeAppUserUuid : draftAssigneeUuid) || null;
    const existingTaskUuid = selectedTask?.workItemUuid;

    if (onAddChild) {
      // Clear the UI immediately so the input doesn't linger while the API is in-flight
      const titleSnapshot = inputValue.trim();
      const taskSnapshot = selectedTask;
      setInputValue('');
      setSelectedTask(null);
      setDraftAssigneeUuid(null);
      setDraftTypeUuid(null);
      setExpandedMode(null);

      try {
        setIsAdding(true);
        // When linking an existing task, pass its current field values so the handler
        // can preserve them rather than resetting to TODO/MEDIUM defaults.
        const existingTaskData = taskSnapshot ? {
          workItemKey: taskSnapshot.workItemKey,
          workStatusUuid: taskSnapshot.workStatusUuid,
          workPriority: taskSnapshot.workPriority,
          workItemAssigneeAppUserUuid: taskSnapshot.workItemAssigneeAppUserUuid,
          workItemReporterAppUserUuid: (taskSnapshot as any).workItemReporterAppUserUuid,
          workItemDescription: (taskSnapshot as any).description || (taskSnapshot as any).workItemDescription || '',
          projectLabelUuidList: (taskSnapshot as any).projectLabelUuidList || [],
          workItemParentWorkItemUuid: taskSnapshot.parentWorkItemUuid || (taskSnapshot as any).workItemParentWorkItemUuid || null,
          parentWorkItemUuid: taskSnapshot.parentWorkItemUuid || (taskSnapshot as any).workItemParentWorkItemUuid || null,
        } : undefined;
        await onAddChild(
          titleSnapshot,
          selectedType,
          selectedAssignee,
          isSubtaskMode ? draftPriority : 'medium',
          existingTaskUuid,
          existingTaskData,
        );

        // For existing items: handleChildAdd intentionally skips the task-children-infinite
        // invalidation because the item's old createdAt would sort it away from top.
        // We call reorderTask here to move it to position 1 on the server, then do the
        // invalidation in onSettled — so the refetch sees the correct server order.
        if (existingTaskUuid && projectId) {
          reorderTask(
            {
              projectUuid: projectId,
              workItemUuid: existingTaskUuid,
              prevWorkItemUuid: null,
              nextWorkItemUuid: null,
              viewType: 'LIST',
              moveToTop: true,
              newStatusUuid: existingTaskData?.workStatusUuid || '',
              previousStatusUuid: existingTaskData?.workStatusUuid || '',
            },
            {
              onSettled: () => {
                // Now that the server has moved the item to top, a refetch will
                // return it in the correct position.
                queryClient.invalidateQueries({
                  queryKey: ['task-children-infinite', projectId, currentTaskUuid],
                  exact: false,
                });
              },
            },
          );
        }
      } finally {
        setIsAdding(false);
      }
      return;
    }

    const newChild: ChildItemDraft = {
      id: Date.now().toString(),
      title: inputValue.trim(),
      workTypeUuid: selectedType,
      workTypeCategory: availableWorkTypes.find(wt => wt.workTypeUuid === selectedType)?.workTypeCategory || null,
      assigneeUuid: draftAssigneeUuid,
      priority: isSubtaskMode ? draftPriority : 'medium',
    };

    onChildrenChange([newChild, ...childItems]);
    setInputValue('');
    setDraftAssigneeUuid(null);
    setDraftTypeUuid(null);
    setExpandedMode(null);
  };

  // Convert ChildItemDraft[] to Task[] for the AddTaskTableLayer recursively
  const mapRecursive = (items: any[]): Task[] => {
    return items.map((item) => {
      const mapped: Task = {
        ...item,
        workItemUuid: item.workItemUuid || item.id,
        workItemKey: item.workItemKey || 'Draft',
        title: item.title || item.workItemTitle,
        workItemTitle: item.title || item.workItemTitle,
        workStatusUuid: item.workStatusUuid,
        workStatusName: item.workStatusName,
        workStatusCategory: item.workStatusCategory,
        workTypeUuid: item.workTypeUuid || '',
        workItemAssigneeAppUserUuid: item.workItemAssigneeAppUserUuid || item.assigneeUuid || '',
        workItemReporterAppUserUuid: item.workItemReporterAppUserUuid || item.reporterUuid || '',
        workPriority: ((item.workPriority || item.priority)?.toUpperCase() as any) || 'MEDIUM',
        createdDate: item.createdDate || new Date().toISOString(),
        lastModifiedAt: item.lastModifiedAt || new Date().toISOString(),
        totalSubTasksCount: item.totalSubTasksCount || 0,
        totalChildWorkItemsCount: item.totalChildWorkItemsCount || 0,
        totalCommentsCount: item.totalCommentsCount || 0,
        totalMediaCount: item.totalMediaCount || 0,
        childWorkItems: item.childWorkItems ? mapRecursive(item.childWorkItems) : [],
        childTotalElements: item.childTotalElements || 0,
      } as any;
      return mapped;
    });
  };

  const mappedTasks: Task[] = mapRecursive(childItems);

  // Table wrapper handlers to update the local state correctly
  const updateDraftItem = (taskId: string, updates: Partial<ChildItemDraft>) => {
    onChildrenChange(
      childItems.map(function updateRecursive(c: any): any {
        if (c.id === taskId || c.workItemUuid === taskId) {
          return { ...c, ...updates };
        }
        if (c.childWorkItems && c.childWorkItems.length > 0) {
          return { ...c, childWorkItems: c.childWorkItems.map(updateRecursive) };
        }
        return c;
      })
    );
  };

  const handleUpdateTask = (taskId: string, updates: Partial<Task>) => {
    updateDraftItem(taskId, { title: updates.title || updates.workItemTitle });
    onUpdateChildItem?.(taskId, updates);
  };

  const handleStatusChange = (taskId: string, statusUuid: string) => {
    updateDraftItem(taskId, { workStatusUuid: statusUuid });
    onUpdateChildItem?.(taskId, { workStatusUuid: statusUuid });
  };

  const handleAssigneeChange = (taskId: string, assignee: User | null) => {
    const newUuid = assignee?.appUserUuid || null;
    updateDraftItem(taskId, { assigneeUuid: newUuid, workItemAssigneeAppUserUuid: newUuid } as any);
    onUpdateChildItem?.(taskId, { workItemAssigneeAppUserUuid: newUuid });
  };

  const handleReporterChange = (taskId: string, reporter: User | null) => {
    const newUuid = reporter?.appUserUuid || null;
    updateDraftItem(taskId, { reporterUuid: newUuid, workItemReporterAppUserUuid: newUuid } as any);
    onUpdateChildItem?.(taskId, { workItemReporterAppUserUuid: newUuid });
  };

  const handlePriorityChange = (taskId: string, priority: string) => {
    updateDraftItem(taskId, { priority: priority.toLowerCase(), workPriority: priority } as any);
    onUpdateChildItem?.(taskId, { workPriority: priority });
  };

  const handleRemoveChild = async (task: any) => {
    // If it's a real task (not a draft), open the confirmation modal
    const uuid = task.workItemUuid || task.id;
    if (onDeleteChild && uuid && typeof uuid === 'string' && uuid.includes('-') && uuid.length > 10) {
      setTaskToDelete(task);
      setDeleteModalOpen(true);
    } else {
      // Local removal for drafts
      const targetId = task.id || task.workItemUuid;
      onChildrenChange(childItems.filter((c) => c.id !== targetId));
    }
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete || !onDeleteChild) return;

    try {
      setIsDeletingTask(true);
      const uuid = (taskToDelete as any).workItemUuid || (taskToDelete as any).id;
      const success = await onDeleteChild(uuid);
      if (success) {
        onChildrenChange(childItems.filter((c) => (c.workItemUuid || c.id) !== uuid));
        setDeleteModalOpen(false);
        setTaskToDelete(null);
        toast.success('Child item deleted successfully');
      }
    } catch (error) {
      console.error('Failed to delete child item:', error);
      toast.error('Failed to delete child item');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const titleText = isSubtaskMode ? 'Add subtask' : 'Add Child work items';

  // Map ProjectUser[] to User[] for dropdowns
  const mappedUsers: User[] = availableUsers.map(u => ({
    id: u.appUserUuid,
    appUserUuid: u.appUserUuid,
    name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
    avatar: u.profileImageURL || u.avatar || '',
    email: u.email || '',
    profileImageURL: u.profileImageURL || u.avatar || '',
  }));

  const filteredMappedTasks = mappedTasks.filter((task) => {
    if (activeFilters.statuses.length > 0 && !activeFilters.statuses.includes(task.workStatusUuid)) return false;
    if (activeFilters.priorities.length > 0 && !activeFilters.priorities.includes(task.workPriority?.toUpperCase() || '')) return false;
    if (activeFilters.taskTypes.length > 0 && !activeFilters.taskTypes.includes(task.workTypeUuid)) return false;
    if (activeFilters.reporters && task.workItemReporterAppUserUuid !== activeFilters.reporters) return false;
    if (activeFilters.hideCompleted) {
      const category = (task.workStatusCategory || '').toUpperCase();
      if (category === 'DONE' || category === 'COMPLETED') return false;
    }
    return true;
  });

  const searchResults = tasksList
    .filter((task) => {
      // 1. Mandatory requirement: Only Story, Task, and Bug can be linked as existing items
      const linkableCategories = ['STORY', 'TASK', 'BUG'];
      if (!linkableCategories.includes(task.workTypeCategory || '')) return false;

      // 2. Hierarchy rule: The item's category must be an allowed child of the current parent task
      if (!allowedChildCategories.includes(task.workTypeCategory)) return false;

      // 3. Prevent self-linking to avoid circularity
      if (currentTaskUuid && task.workItemUuid === currentTaskUuid) return false;

      // 4. Duplicate rule: Exclude items already in the child list
      if (childItems.some(item => (item.id === task.workItemUuid || item.workItemUuid === task.workItemUuid))) return false;

      return true;
    })
    .map((task) => {
      const parentUuid = task.parentWorkItemUuid || (task as any).workItemParentWorkItemUuid;
      const parentTaskObj = parentUuid ? tasksList.find(t => t.workItemUuid === parentUuid) : null;
      return {
        ...task,
        hasExistingParent: !!parentUuid,
        parentWorkItemKey: parentTaskObj?.workItemKey || '',
      };
    });

  const getStatusSummary = () => {
    if (totalOpenWorkItemCount !== undefined || totalInProgressWorkItemCount !== undefined || totalDoneWorkItemCount !== undefined) {
      return {
        TO_DO: totalOpenWorkItemCount || 0,
        IN_PROGRESS: totalInProgressWorkItemCount || 0,
        DONE: totalDoneWorkItemCount || 0,
      };
    }

    // Fallback to local count (only works if all items are loaded)
    const counts = { TO_DO: 0, IN_PROGRESS: 0, DONE: 0 };
    childItems.forEach(item => {
      const cat = (item.workStatusCategory || 'TO_DO').toUpperCase();
      if (cat === 'DONE' || cat === 'COMPLETED') counts.DONE++;
      else if (cat === 'IN_PROGRESS') counts.IN_PROGRESS++;
      else counts.TO_DO++;
    });
    return counts;
  };

  const statusSummary = getStatusSummary();

  return (
    <div className="w-full mt-6 relative z-20">
      <ChildItemsHeader
        titleText={titleText}
        isSubtaskMode={isSubtaskMode}
        expandedMode={expandedMode}
        onToggleCreate={() => {
          setExpandedMode(expandedMode === 'create' ? null : 'create');
          setInputValue('');
          setSelectedTask(null);
        }}
        onToggleLink={() => {
          setExpandedMode(expandedMode === 'link' ? null : 'link');
          setInputValue('');
          setSelectedTask(null);
        }}
        isFilterOpen={isFilterOpen}
        onToggleFilter={() => setIsFilterOpen(!isFilterOpen)}
        hasActiveFilters={!!hasActiveFilters}
        getActiveFilterCount={getActiveFilterCount}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        readOnly={readOnly}
      />

      <TaskFilterDropdown
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        assigneeOptions={mappedUsers}
        reporterOptions={mappedUsers}
        workStatuses={availableWorkStatuses || []}
        workTypes={availableWorkTypes || []}
        currentFilters={activeFilters}
      />

      {expandedMode === 'create' && !readOnly && (
        <ChildItemsCreateForm
          inputValue={inputValue}
          onInputChange={setInputValue}
          draftTypeUuid={draftTypeUuid}
          setDraftTypeUuid={setDraftTypeUuid}
          draftAssigneeUuid={draftAssigneeUuid}
          setDraftAssigneeUuid={setDraftAssigneeUuid}
          allowedChildTypes={allowedChildTypes}
          availableUsers={availableUsers}
          isSubtaskMode={isSubtaskMode}
          isAdding={isAdding}
          disabled={disabled}
          onAdd={handleAddChild}
          onCancel={() => {
            setInputValue('');
            setDraftAssigneeUuid(null);
            setExpandedMode(null);
          }}
        />
      )}

      {expandedMode === 'link' && !readOnly && (
        <ChildItemsLinkCombobox
          inputValue={inputValue}
          onInputChange={setInputValue}
          selectedTask={selectedTask}
          onSelectTask={setSelectedTask}
          searchResults={searchResults}
          isAdding={isAdding}
          disabled={disabled}
          onAdd={handleAddChild}
          showParentBadge={true}
          onCancel={() => {
            setInputValue('');
            setSelectedTask(null);
            setExpandedMode(null);
          }}
        />
      )}

      {projectId && (childItems.length > 0 || hasActiveFilters || isLoading) && (
        <ChildItemsTable
          filteredMappedTasks={filteredMappedTasks}
          childItems={childItems}
          mappedUsers={mappedUsers}
          projectId={projectId}
          projectKey={projectKey!}
          isCompact={isCompact}
          isSubtaskMode={isSubtaskMode}
          isLoading={isLoading}
          isReordering={isMovingToBottom}
          isRemoving={isRemoving}
          isLoadingMore={isLoadingMore}
          childTotalElements={childTotalElements}
          externalTotalCount={externalTotalCount}
          statusSummary={statusSummary}
          hasActiveFilters={!!hasActiveFilters}
          activeFilters={activeFilters}
          onUpdateTask={handleUpdateTask}
          onAssigneeChange={handleAssigneeChange}
          onReporterChange={handleReporterChange}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onRemove={handleRemoveChild}
          onLoadMoreChildren={onLoadMoreChildren}
          onExpandChild={onExpandChild}
          onMoveToTop={handleMoveToTop}
          onMoveToBottom={handleMoveToBottom}
          onReorderWorkItem={hasActiveFilters || activeFilters?.sortBy !== 'listOrder' || isReorderPending ? undefined : handleReorderChild}
          loadingParents={loadingParents}
          onClearFilters={handleClearFilters}
          onStartCreate={() => {
            setExpandedMode('create');
            setInputValue('');
          }}
          readOnly={readOnly}
        />
      )}

      {/* Delete Confirmation Modal */}
      {taskToDelete && (
        <EnhancedDeleteModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setTaskToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeletingTask}
          entityType="WORK_ITEM"
          entityKey={taskToDelete.workItemKey}
          workTypeCategory={taskToDelete.workTypeCategory}
          counts={{
            subtasks: countAllNestedChildren(taskToDelete),
            comments: taskToDelete.totalCommentsCount || 0,
            attachments: taskToDelete.totalMediaCount || 0,
          }}
        />
      )}
    </div>
  );
});

export default ChildItemsList;
